import { z } from 'zod'

import {
  dashboardAnalyticsQueryRequestSchema,
  dashboardAnalyticsResponseSchema,
  dashboardCreateRequestSchema,
  dashboardSchema,
  dashboardUpdateRequestSchema,
  dashboardWidgetSchema,
  dashboardWidgetCreateRequestSchema,
  dashboardWidgetUpdateRequestSchema,
  widgetVisibilityRequestSchema,
  type Dashboard,
  type DashboardAnalyticsQueryRequest,
  type DashboardAnalyticsResponse,
  type DashboardCreateRequest,
  type DashboardUpdateRequest,
  type DashboardWidget,
  type DashboardWidgetCreateRequest,
  type DashboardWidgetUpdateRequest,
  type WidgetVisibilityRequest,
  type WidgetReorderRequest,
  widgetReorderRequestSchema,
} from '@/types/api/dashboard'

import { apiClient } from './client'

export async function getDashboards(): Promise<Dashboard[]> {
  const response = await apiClient.get('/dashboards')
  return z.array(dashboardSchema).parse(response.data)
}

export async function getDefaultDashboard(): Promise<Dashboard> {
  const response = await apiClient.get('/dashboards/default')
  return dashboardSchema.parse(response.data)
}

/** A record group's own dashboard (created on first open by someone who may edit the group). */
export async function getRecordGroupDashboard(recordGroupId: string): Promise<Dashboard> {
  const response = await apiClient.get(`/dashboards/record-group/${recordGroupId}`)
  return dashboardSchema.parse(response.data)
}

export async function getDashboard(dashboardId: string): Promise<Dashboard> {
  const response = await apiClient.get(`/dashboards/${dashboardId}`)
  return dashboardSchema.parse(response.data)
}

export async function createDashboard(data: DashboardCreateRequest): Promise<Dashboard> {
  const validated = dashboardCreateRequestSchema.parse(data)
  const response = await apiClient.post('/dashboards', validated)
  return dashboardSchema.parse(response.data)
}

export async function updateDashboard(dashboardId: string, data: DashboardUpdateRequest): Promise<Dashboard> {
  const validated = dashboardUpdateRequestSchema.parse(data)
  const response = await apiClient.put(`/dashboards/${dashboardId}`, validated)
  return dashboardSchema.parse(response.data)
}

export async function deleteDashboard(dashboardId: string): Promise<void> {
  await apiClient.delete(`/dashboards/${dashboardId}`)
}

export async function queryDashboardAnalytics(data: DashboardAnalyticsQueryRequest): Promise<DashboardAnalyticsResponse> {
  const validated = dashboardAnalyticsQueryRequestSchema.parse(data)
  const response = await apiClient.post('/dashboards/analytics/query', validated)
  return dashboardAnalyticsResponseSchema.parse(response.data)
}

// ---- Widget CRUD ----

export async function createWidget(
  dashboardId: string,
  data: DashboardWidgetCreateRequest
): Promise<DashboardWidget> {
  const validated = dashboardWidgetCreateRequestSchema.parse(data)
  const response = await apiClient.post(`/dashboards/${dashboardId}/widgets`, validated)
  return dashboardWidgetSchema.parse(response.data)
}

export async function updateWidget(
  dashboardId: string,
  widgetId: string,
  data: DashboardWidgetUpdateRequest
): Promise<DashboardWidget> {
  const validated = dashboardWidgetUpdateRequestSchema.parse(data)
  const response = await apiClient.put(`/dashboards/${dashboardId}/widgets/${widgetId}`, validated)
  return dashboardWidgetSchema.parse(response.data)
}

export async function deleteWidget(dashboardId: string, widgetId: string): Promise<void> {
  await apiClient.delete(`/dashboards/${dashboardId}/widgets/${widgetId}`)
}

export async function updateWidgetVisibility(
  dashboardId: string,
  widgetId: string,
  data: WidgetVisibilityRequest
): Promise<void> {
  const validated = widgetVisibilityRequestSchema.parse(data)
  await apiClient.put(`/dashboards/${dashboardId}/widgets/${widgetId}/visibility`, validated)
}

export async function reorderWidgets(
  dashboardId: string,
  data: WidgetReorderRequest
): Promise<void> {
  const validated = widgetReorderRequestSchema.parse(data)
  await apiClient.put(`/dashboards/${dashboardId}/widgets/reorder`, validated)
}

export async function moveWidget(
  dashboardId: string,
  widgetId: string,
  x: number,
  y: number
): Promise<DashboardWidget> {
  const response = await apiClient.put(`/dashboards/${dashboardId}/widgets/${widgetId}/position`, { x, y })
  return dashboardWidgetSchema.parse(response.data)
}

export async function resizeWidget(
  dashboardId: string,
  widgetId: string,
  w: number,
  h: number
): Promise<DashboardWidget> {
  const response = await apiClient.put(`/dashboards/${dashboardId}/widgets/${widgetId}/size`, { w, h })
  return dashboardWidgetSchema.parse(response.data)
}
